import { config, IVectorLayerStyles, mapTileGrid } from "./config";
import { getMap } from "./GeoLiteMap";
import { Geometry, Point, SimpleGeometry } from "ol/geom";
import { extend, Extent, buffer } from "ol/extent";
import { getPointResolution } from "ol/proj";
import { Feature, View, Map, MapBrowserEvent } from "ol";
import { getBasemapLayers } from "./basemapFunctions";
import { getDataLayers } from "./layerFunctions";
import { AttributionButton } from "./Buttons/Attribution";
import { defaults as defaultControls, Zoom } from "ol/control";
import { getDrawingSymbol, getHighLightSymbol } from "./Symbols/symbols";
import { Vector as VectorLayer } from "ol/layer";
import { IFeatureArrayTimeStamp } from "../Contexts/AppContext";
import { Vector as VectorSource, Cluster as ClusterSource } from "ol/source";
import { DrawType, SheetSelectMode } from "./enums";
import { debounce, deepClone, deepCloneWithChanges, getUrlParam, replaceUrlParam, roundToPlaceValue } from "./utils";
import { IHoveredCluster } from "../Contexts/MapContext";
import { getSimpleSymbol } from "./Symbols/clusterSymbols";
import { Coordinate } from "ol/coordinate";
import { animatePoint, clusterCircularExpand, clusterExpand } from "./clusterAnimation";
import { FeatureLike } from "ol/Feature";
import { identifyClusterFeatures } from "./Identify/identifyUtils";

let DEFAULT_DPI;
const inchesPerMeter = 1000 / 25.4;

const loc = getUrlParam("loc");
const locData = loc && loc.split(";");

let mapLods;

const getBuffer = (feature: Feature) => buffer(feature.getGeometry().getExtent(), 100);

export const createMap = (
    configA,
    target: HTMLDivElement,
    highLightSource: VectorSource<Geometry>,
    highLightClusterSource: VectorSource<Geometry>,
    highLightSheetSource: VectorSource<Geometry>,
    hoveredSource: VectorSource<Geometry>,
    clickedClusterSource: VectorSource<Geometry>
): Map => {
    DEFAULT_DPI = configA.defaultDpi || 96.198892;
    mapLods = configA.mapLods;
    const configResolutions = configA.mapLods && configA.mapLods.map((lod) => lod.resolution);
    const defaultTileGrid = mapTileGrid;
    configResolutions && (defaultTileGrid.resolutions = configResolutions);

    const center =
        locData && locData.length === 3 ? [parseInt(locData[0], 10), parseInt(locData[1], 10)] : config.default.center;
    const zoom = locData && locData.length === 3 ? parseInt(locData[2], 10) : config.default.zoom;

    const highLightLayer = new VectorLayer({
        properties: { id: config.default.highLightLayerId },
        source: highLightSource,
        zIndex: 3000,
        style: (f) => getHighLightSymbol(f as Feature<Geometry>, configA.identifyGeometries)
    });

    const highLightClusterLayer = new VectorLayer({
        properties: { id: config.default.highLightClusterLayerId },
        source: highLightClusterSource,
        zIndex: 6000
    });

    const highLightSheetLayer = new VectorLayer({
        properties: { id: config.default.highLightSheetLayerId },
        source: highLightSheetSource,
        zIndex: 3000,
        style: (f) => getHighLightSymbol(f as Feature<Geometry>, configA.identifyGeometries)
    });

    const hoveredLayer = new VectorLayer({
        properties: { id: config.default.hoverLayerId },
        source: hoveredSource,
        zIndex: 4000,
        style: null
    });

    const clickedClusterLayer = new VectorLayer({
        properties: { id: config.default.clickedClusterLayerId },
        source: clickedClusterSource,
        zIndex: 3000,
        style: null
    });

    const drawingLayer = new VectorLayer({
        properties: { id: config.default.drawingLayerId, type: "FeatureLayer" },
        source: new VectorSource({ useSpatialIndex: false }), // useSpatialIndex: false is to maintain same order in feature collection
        zIndex: 2000,
        style: (drawFeature) =>
            getDrawingSymbol(
                drawFeature,
                drawFeature.get("MEASUREMENTSHIDDEN"),
                drawFeature.get("DRAWTYPE"),
                drawFeature.get("COLOR")
            )
    });

    const map = new Map({
        layers: [
            ...getBasemapLayers(configA),
            ...getDataLayers(configA),
            drawingLayer,
            highLightLayer,
            highLightClusterLayer,
            highLightSheetLayer,
            hoveredLayer,
            clickedClusterLayer
        ],
        target,
        view: new View({
            projection: config.default.projection,
            center,
            extent: config.default.viewExtent,
            zoom,
            enableRotation: false,
            showFullExtent: true,
            constrainResolution: true,
            resolutions: configResolutions || defaultTileGrid.resolutions
        }),
        controls: defaultControls({ zoom: false, rotate: false, attribution: false }).extend([
            new AttributionButton(),
            new Zoom({
                className: "zoom-buttons",
                target: document.getElementById("zoom-button-container") as HTMLElement
            })
        ])
    });

    const startExt = configA.startingExtent;
    startExt && !loc && map.getView().fit([startExt.xmin, startExt.ymin, startExt.xmax, startExt.ymax]);

    return map;
};

export const zoomHome = (callback?: () => void): void => {
    const view = getMap().getView();
    //@ts-ignore
    const resolutionX = config.default.extentSize[0] / view.viewportSize_[0];
    //@ts-ignore
    const resolutionY = config.default.extentSize[1] / view.viewportSize_[1];
    const zoom = view.getZoomForResolution(resolutionX > resolutionY ? resolutionX : resolutionY);

    view.fit(new Point(config.default.center), {
        maxZoom: zoom,
        duration: 1000,
        callback
    });
};

export const zoomToCoordinates = (coordinates: Coordinate): void => zoomTo(new Point(coordinates));
export const zoomToFeature = (feature: Feature): void => zoomTo(feature.getGeometry());

const zoomTo = (geometry: Geometry): void => {
    const view = getMap().getView();
    const currentZoom = view.getZoom();
    const maxZoom = currentZoom > config.default.zoomToLevel ? currentZoom : config.default.zoomToLevel;
    document.dispatchEvent(new Event("stopTracking"));
    view.fit(geometry as SimpleGeometry, { duration: 500, maxZoom });
};

export const zoomToExtent = (extent: Extent, callback?: () => void): void => {
    if (!extent || extent.includes(Infinity)) {
        return;
    }
    document.dispatchEvent(new Event("stopTracking"));
    const view = getMap().getView();
    view.fit(extent, { duration: 500, padding: [20, 20, 20, 20], callback });
};

export const onMoveEnd = (): void => {
    const view = getMap().getView();
    const center = view.getCenter();
    const zoom = view.getZoom();
    const scale = getScale(zoom);
    //@ts-ignore
    document.mapScale = scale;
    document.dispatchEvent(new CustomEvent("moveend", { detail: scale }));

    const params = Math.round(center[0]) + ";" + Math.round(center[1]) + ";" + Math.round(zoom);
    const url = replaceUrlParam("loc", params);
    window.history.replaceState({}, document.title, url);
};

const getScale = (zoom: number): number => {
    const view = getMap().getView();
    const center = view.getCenter();
    const pointResolution = getPointResolution(view.getProjection(), view.getResolution(), center, "m");

    return mapLods
        ? mapLods.find((lod) => lod.level === zoom).scale
        : roundToPlaceValue(Math.round(parseFloat(pointResolution.toString()) * inchesPerMeter * DEFAULT_DPI));
};

export const onPointerMove = (evt, target: HTMLDivElement, configA, setHoveredCluster, setHoveredFeature): void => {
    debounce(handlePointerMove(evt, target, configA, setHoveredCluster, setHoveredFeature), 100);
};

const handlePointerMove = (evt, target: HTMLDivElement, configA, setHoveredCluster, setHoveredFeature): void => {
    if (evt.dragging) {
        return;
    }

    const hasFeatureAtPixel = getMap().hasFeatureAtPixel(evt.pixel);
    target.style.cursor = hasFeatureAtPixel ? "pointer" : null;

    const hasClusterFeatureAtPixel = getMap().hasFeatureAtPixel(evt.pixel, {
        hitTolerance: 10,
        layerFilter: (layer) => layer.getSource() instanceof ClusterSource
    });
    handleClusterHover(hasClusterFeatureAtPixel, configA.layers, evt.pixel, setHoveredCluster);

    const tooltipFeaturesAtPixel = getMap().getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => layer.get("isTooltipLayer"),
        hitTolerance: 8
    });
    resetHoveredFeature();
    if (tooltipFeaturesAtPixel.length && !hasClusterFeatureAtPixel) {
        let hasHoveredFeature;
        getMap().forEachFeatureAtPixel(
            evt.pixel,
            (pixelFeature, layer) => {
                !hasHoveredFeature &&
                    (layer.getSource() as VectorSource).getFeatures().map((feature) => {
                        if (pixelFeature === feature) {
                            hasHoveredFeature = true;
                            feature.set("HOVERED", true);
                            setHoveredFeature({
                                feature,
                                evt: evt.coordinate,
                                tooltipTemplate: layer.get("tooltip").tooltipTemplate,
                                fieldInfo: layer.get("fieldInfo")
                            });
                        }
                    });
            },
            {
                layerFilter: (layer) => layer.get("isTooltipLayer")
            }
        );
    } else {
        setHoveredFeature(null);
    }
};

const handleClusterHover = (hasFeature, configLayers, pixel, setHoveredCluster) => {
    if (hasFeature) {
        const clusterLayers = configLayers.filter((l) => l.type === "ClusterLayer");
        for (const clusterLayer of clusterLayers) {
            const pixelfeature = getMap().getFeaturesAtPixel(pixel, {
                hitTolerance: 10,
                layerFilter: (layer) => layer.get("id") === clusterLayer.id
            });
            if (pixelfeature && pixelfeature.length && !pixelfeature[0].get("ISCLUSTERCLICKED")) {
                (pixelfeature[0] as Feature).set("HOVERED", true);

                const clusterConfig =
                    clusterLayer.cluster ||
                    configLayers.find((configLayer) => configLayer.id === clusterLayer.clusterSharingLayer).cluster;

                setHoveredCluster({
                    feature: pixelfeature[0],
                    style: removePaddings(clusterLayer.vectorStyles),
                    clusterConfig: clusterConfig
                });
                break;
            }
        }
    } else {
        const clusterLayers = configLayers.filter((l) => l.type === "ClusterLayer");
        clusterLayers && resetHoveredCluster(clusterLayers);
        clusterLayers && setHoveredCluster(null);
    }
};

const removePaddings = (vectorStyles: IVectorLayerStyles) =>
    vectorStyles.labels
        ? deepCloneWithChanges(vectorStyles, [
              { key: "regularShape", remove: true },
              { key: "offsetX", remove: true },
              { key: "padding", value: [4, 4, 2, 5] }
          ])
        : vectorStyles;

export const onClick = (
    evt: MapBrowserEvent<any>,
    setMapClick: CallableFunction,
    setFeaturesPopup: CallableFunction,
    drawType: DrawType,
    highLightClusterSource: VectorSource,
    clickedClusterSource: VectorSource
): void => {
    if (evt.dragging || drawType) {
        return;
    }

    const clusterFeatures = getMap().getFeaturesAtPixel(evt.pixel, {
        layerFilter: (layer) => layer.getSource() instanceof ClusterSource
    });
    if (clusterFeatures && clusterFeatures.length) {
        (clusterFeatures[0] as Feature).get("features")?.length > 1
            ? onClusterSourceClick(clusterFeatures[0], setFeaturesPopup, highLightClusterSource, clickedClusterSource)
            : setMapClick(evt);

        return;
    }

    const hasFeatureAtPixel = getMap().hasFeatureAtPixel(evt.pixel, {
        layerFilter: (layer) => layer.get("id") === config.default.drawingLayerId
    });

    if (!hasFeatureAtPixel) {
        setMapClick(evt);
        return;
    }

    console.log("map click");
};

export const onDoubleClick = (
    evt: MapBrowserEvent<any>,
    setMapClick: CallableFunction,
    drawType: DrawType,
    sheetSelectMode: SheetSelectMode
): void => {
    if (sheetSelectMode && sheetSelectMode === SheetSelectMode.Polygon) {
        setMapClick(evt);
        return;
    }

    if (evt.dragging || drawType) {
        return;
    }

    const hasClusterAtPixel = getMap().hasFeatureAtPixel(evt.pixel, {
        layerFilter: (layer) => layer.getSource() instanceof ClusterSource
    });
    if (hasClusterAtPixel) {
        getMap().forEachFeatureAtPixel(evt.pixel, (f) => {
            const clusteredFeatures: Feature[] = f.get("features");
            if (clusteredFeatures?.length >= 1) {
                zoomToExtent(
                    clusteredFeatures.reduce<Extent>(
                        (result, feature) =>
                            result === null ? getBuffer(feature) : extend(result, getBuffer(feature)),
                        null
                    )
                );
            }
        });
        return;
    }
};

const onClusterSourceClick = (
    feature: FeatureLike,
    setFeaturesPopup,
    highLightClusterSource: VectorSource,
    clickedClusterSource: VectorSource
) => {
    if ((feature as Feature).get("ISCLUSTERCLICKED")) {
        return;
    }
    resetClickedCluster(clickedClusterSource);
    (feature as Feature).set("ISCLUSTERCLICKED", true);
    const clusteredFeatures: Feature[] = feature.get("features");
    if (clusteredFeatures?.length >= 1) {
        clusteredFeatures[0].get("layerData").cluster?.identifyOnClick
            ? identifyClickedCluster(
                  feature,
                  clusteredFeatures,
                  setFeaturesPopup,
                  highLightClusterSource,
                  clickedClusterSource
              )
            : zoomToExtent(
                  clusteredFeatures.reduce<Extent>(
                      (result, feature) => (result === null ? getBuffer(feature) : extend(result, getBuffer(feature))),
                      null
                  )
              );
    }
};

const identifyClickedCluster = (
    feature,
    clusteredFeatures,
    setFeaturesPopup,
    highLightClusterSource,
    clickedClusterSource
) => {
    const center = (feature.getGeometry() as Point).getCoordinates();
    const view = getMap().getView();
    const pointResolution = getPointResolution(view.getProjection(), view.getResolution(), center, "m");
    setFeaturesPopup({
        coordinates: [center[0], center[1] + 32 * pointResolution],
        results: identifyClusterFeatures(clusteredFeatures),
        hideArrow: true,
        hideHighLight: true
    });

    const vectorStyles = clusteredFeatures[0].get("layerData").vectorStyles;
    const noLabels = vectorStyles.labels && clusteredFeatures[0].get("layerData").cluster?.hoverAnimation?.hideLabels;
    const styles = { ...vectorStyles };
    if (noLabels) {
        delete styles.labels;
    }

    prepareClickedClusterFeatures(feature, clickedClusterSource, styles);
    animateClickedCluster(clusteredFeatures, highLightClusterSource, clickedClusterSource);
};

export const changeHighlightFeatures = (
    highLightFeatures: IFeatureArrayTimeStamp,
    highLightSource: VectorSource<Geometry>
): void => {
    if (!highLightFeatures?.features) {
        highLightSource.clear();
        return;
    }
    highLightSource.clear();
    highLightSource.addFeatures(highLightFeatures.features);
};

export const changeHighlightSheetFeatures = (
    sheetFeatures: Feature[],
    highLightSheetSource: VectorSource<Geometry>
): void => {
    if (!sheetFeatures) {
        highLightSheetSource.clear();
        return;
    }
    highLightSheetSource.clear();
    highLightSheetSource.addFeatures(sheetFeatures);
};

export const highlightCluster = (
    clusterFeature: IHoveredCluster,
    highLightClusterSource: VectorSource<Geometry>
): void => {
    const features = prepareClusterFeatures(clusterFeature, highLightClusterSource);
    if (!features) {
        return;
    }

    highLightClusterSource.addFeatures(features);
};

export const onHoverCluster = (
    hoveredCluster: IHoveredCluster,
    animationId: number,
    setAnimationId: CallableFunction,
    animating: boolean,
    setAnimating: CallableFunction,
    highLightClusterSource: VectorSource
): void => {
    if (
        hoveredCluster &&
        hoveredCluster.clusterConfig.hoverAnimation &&
        !animating &&
        hoveredCluster.feature.get("features")?.length > 1
    ) {
        const id = hoveredCluster.feature.get("features")[0].get("OBJECTID");
        if (id !== animationId) {
            setAnimationId(id);
            animateCluster(hoveredCluster, highLightClusterSource);
            setTimeout(() => setAnimating(false), hoveredCluster.clusterConfig.hoverAnimation.durationMs + 50);
        }
    }
    if (hoveredCluster && !hoveredCluster.clusterConfig.hoverAnimation) {
        highlightCluster(hoveredCluster, highLightClusterSource);
    }
    if (!hoveredCluster) {
        highlightCluster(null, highLightClusterSource);
        animationId && setAnimationId(null);
    }
};

export const animateCluster = (
    clusterFeature: IHoveredCluster,
    highLightClusterSource: VectorSource<Geometry>
): void => {
    const features = prepareClusterFeatures(clusterFeature, highLightClusterSource);
    if (!features) {
        return;
    }

    const startCoords = (clusterFeature.feature.getGeometry() as Point).getCoordinates();
    const clones = features.map((f: Feature) => {
        const newFeat = f.clone();
        (newFeat.getGeometry() as Point).setCoordinates(startCoords);
        newFeat.set("LABELCOORDS", []);
        return newFeat;
    });
    highLightClusterSource.addFeatures(clones);

    clusterFeature.clusterConfig.hoverAnimation.circularExpand
        ? clusterCircularExpand(
              clones,
              startCoords,
              clusterFeature.clusterConfig.hoverAnimation.durationMs,
              clusterFeature.clusterConfig.hoverAnimation.pointRadiusPx
          )
        : clusterExpand(clones, features, startCoords, clusterFeature.clusterConfig.hoverAnimation.durationMs);
};

export const animateClickedCluster = (
    features: Feature[],
    highLightClusterSource: VectorSource<Geometry>,
    clickedClusterSource: VectorSource<Geometry>
): void => {
    const keepCircularOnClick = features[0].get("layerData").cluster?.hoverAnimation?.keepCircularOnClick;
    const vectorStyles = features[0].get("layerData").vectorStyles;

    const clones = features.map((f: Feature, i) => {
        const newFeat = f.clone();
        const targetCoords = (f.getGeometry() as Point).getCoordinates();
        const startCoords = highLightClusterSource.getFeatures()?.length
            ? (highLightClusterSource.getFeatures()[i]?.getGeometry() as Point).getCoordinates()
            : targetCoords;
        (newFeat.getGeometry() as Point).setCoordinates(startCoords);
        if (keepCircularOnClick) {
            const labelCoords = highLightClusterSource.getFeatures()[i]?.get("LABELCOORDS");
            labelCoords && newFeat.set("LABELCOORDS", labelCoords);

            const paddingLessStyle = removePaddings(vectorStyles);
            const style = getSimpleSymbol(newFeat, paddingLessStyle);
            newFeat.setStyle(style);
        } else {
            animatePoint(newFeat, startCoords, targetCoords, 500);
        }

        return newFeat;
    });
    highLightClusterSource.clear();
    clickedClusterSource.addFeatures(clones);
};

const prepareClusterFeatures = (clusterFeature: IHoveredCluster, highLightClusterSource) => {
    if (
        !clusterFeature ||
        !(clusterFeature.clusterConfig.previewOnHover || clusterFeature.clusterConfig.previewOnHover === undefined)
    ) {
        highLightClusterSource.clear();
        return;
    }
    const features = clusterFeature.feature.get("features");
    if (!features || !features?.length) {
        highLightClusterSource.clear();
        return;
    }
    highLightClusterSource.clear();
    const noLabels = clusterFeature.style.labels && clusterFeature.clusterConfig.hoverAnimation.hideLabels;
    const style = deepClone(clusterFeature.style);
    if (noLabels) {
        delete style.labels;
    }
    (
        getMap()
            .getLayers()
            .getArray()
            .find(
                (mapLayer) => mapLayer.get("id") === config.default.highLightClusterLayerId
            ) as VectorLayer<VectorSource>
    ).setStyle((feature) => getSimpleSymbol(feature, style));

    return features;
};

const prepareClickedClusterFeatures = (
    clusterFeature: FeatureLike,
    clickedClusterSource: VectorSource,
    styles: IVectorLayerStyles
) => {
    clickedClusterSource.clear();
    clickedClusterSource.set("CLUSTERFEATURE", clusterFeature as Feature);
    const layer = getMap()
        .getLayers()
        .getArray()
        .find((mapLayer) => mapLayer.get("id") === config.default.clickedClusterLayerId) as VectorLayer<VectorSource>;

    layer.setStyle((feature) => getSimpleSymbol(feature, styles));
};

const resetHoveredCluster = (clusterLayers) =>
    clusterLayers.map((clusterLayer) => {
        const mapClusterLayer = getMap()
            .getLayers()
            .getArray()
            .find((mapLayer) => mapLayer.get("id") === clusterLayer.id) as VectorLayer<VectorSource>;
        mapClusterLayer?.getSource().forEachFeature((f) => f.get("HOVERED") && f.set("HOVERED", false));
    });

const resetHoveredFeature = () =>
    getMap()
        .getLayers()
        .getArray()
        .map(
            (mapLayer) =>
                mapLayer.get("isTooltipLayer") &&
                (mapLayer as VectorLayer<VectorSource>).getSource().forEachFeature((f) => {
                    f.setStyle(null);
                    f.set("HOVERED", false);
                })
        );

export const resetClickedCluster = (clickedClusterSource: VectorSource): void => {
    clickedClusterSource.get("CLUSTERFEATURE")?.set("ISCLUSTERCLICKED", false);
    clickedClusterSource.clear();
};

export const getDrawingLayer = (): VectorLayer<VectorSource<Geometry>> =>
    getMap() &&
    (getMap()
        .getLayers()
        .getArray()
        .find((l) => l.get("id") === config.default.drawingLayerId) as VectorLayer<VectorSource>);
